import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { SIcon } from '@chargebee/sting-react';
import { NavIcons } from '@bbkAdminComponents/app-layout/nav';
import {
  PivotTableComponent,
  type PivotT,
} from '@bbkAdminComponents/pivot-table/pivot-table';
import { PivotTableTooltipContent } from '@bbkAdminComponents/pivot-table/pivot-table-content-tooltip';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/play-context';
import { type CancelPlayReportProps } from '@bbkAdminComponents/unified-plays/reporting/reports/cancel-page/cancel-page-reports';
import {
  selectAppId,
  selectCompanyInternalName,
} from '@bbkAdminRedux/app/selectors';
import {
  selectFromResultTyped,
  toDictionary,
  useAppDispatch,
} from '@bbkAdminRedux/redux_utils';
import {
  appPersistSlice,
  selectExpPerfReportNav,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { billingConfigSlice } from '@bbkAdminRedux/rtkq/billing-config.slice';
import {
  DEFAULT_BRAND_ID,
  useGetBrandOptions,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import {
  useSelectAllFunnelsEntities,
  useSelectFunnelsLoading,
} from '@bbkAdminRedux/rtkq/funnels.slice';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import {
  reportsSlice,
  ReportType,
  type GetReportV2Args,
  type TrendsDatapoint,
  type TrendsReport,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import {
  rulesSlice,
  RuleType,
  useSelectAllRulesEntities,
} from '@bbkAdminRedux/rtkq/rules.slice';
import {
  surveyReasonsSlice,
  useSelectAllVanityReasons,
} from '@bbkAdminRedux/rtkq/survey-reasons.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { NumberKit } from '@bbkAdminUtils/number-kit';
import { usePlans } from '@bbkAdminUtils/plans';
import { useStrings } from '@bbkAdminUtils/strings';

const PlayExpPerf: React.FC<CancelPlayReportProps> = ({ play }) => {
  const dispatch = useAppDispatch();
  const companyInternalName = useSelector(selectCompanyInternalName);
  const appKey = useSelector(selectAppId);
  const brandOptions = useGetBrandOptions({ appKey });
  const strings = useStrings();
  const brandId = play.brand_id ?? DEFAULT_BRAND_ID;

  const selectedBrand = useMemo(() => {
    return brandOptions.find((b) => b.value === brandId);
  }, [brandOptions, brandId]);

  const isDemoData = useShowPlayDemoData();

  console.log(isDemoData);

  const isNestedAudiencesEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_NESTED_AUDIENCES_IN_PERF_REPORTS
  );

  const { isStarterFreePlan } = usePlans();
  const exportBtnTooltip = {
    content: strings.UPGRADE_PLAN({
      description: strings.UPGRADE_TO_STARTER_PREMIUM_EXPORT,
    }),
    tooltipOptions: {
      placement: 'left',
    },
  };

  const { groupBy: initGroupByOpts, visibleColumns: initVisibleColumns } =
    useSelector(selectExpPerfReportNav(appKey));

  const billingIntegration = useSelectBillingIntegration({ appKey });

  const { data: billingConfig } =
    billingConfigSlice.endpoints.getBillingConfig.useQuery(
      billingIntegration ? { app_key: appKey } : skipToken
    );
  const isRepurchaseAttributionEnabled =
    billingConfig?.repurchase_based_attribution;

  const { startDate: start, endDate: end } = useSelectDatePicker();
  const request = {
    report_type: ReportType.trends,
    parameters: {
      group_by_period: 'all',
      group_by: 'funnel_id,audiences,vanity_reason',
    },
  };

  const args: GetReportV2Args = {
    appKey,
    context: isDemoData ? BBKRequestContext.reports_filter : undefined,
    start,
    end,
    report_type_requests: [request],
    filters: [[{ field: 'play', operator: 'equals', value: play.id }]],
  };
  const opts = {
    skip: !appKey || !start || !end,
    selectFromResult: selectFromResultTyped<TrendsReport>,
  };
  const { data: trendsReport, isFetching: isTrendsFetching } =
    reportsSlice.endpoints.getReportV2.useQuery(args, opts);

  const data =
    trendsReport?.trends.filter(
      (t) =>
        t.is_timeperiod_stub === 0 &&
        t.timeGroup !== null &&
        (t.canceled > 0 || t.deflects > 0)
    ) ?? [];

  const rulesArgs = {
    appKey,
    context: BBKRequestContext.exp_perf,
    includeAdditionalRules: [RuleType.SPLIT],
    brandId,
  };
  const { isFetching: isRulesFetching } =
    rulesSlice.endpoints.getRules.useQuery(rulesArgs);
  const rulesMap = useSelectAllRulesEntities(rulesArgs);

  const reasonsArgs = {
    companyInternalName,
    appKey,
    context: BBKRequestContext.exp_perf,
    brandId,
  };
  const vanityReasons = useSelectAllVanityReasons(reasonsArgs);
  const { isFetching: isReasonsFetching } =
    surveyReasonsSlice.endpoints.getReasons.useQuery(reasonsArgs);
  const vanityReasonsMap = toDictionary(
    vanityReasons.map((vr) => ({ ...vr, id: vr.internal_name }))
  );

  const funnelsArgs = { appKey, context: BBKRequestContext.exp_perf, brandId };
  const { isFetching: isFunnelsFetching } =
    useSelectFunnelsLoading(funnelsArgs);

  const funnelsMap = useSelectAllFunnelsEntities(funnelsArgs);

  const isFetching =
    isTrendsFetching ||
    isRulesFetching ||
    isReasonsFetching ||
    isFunnelsFetching;

  type ReportTags = {
    funnelId: string;
    audience1Id: string;
    audience2Id?: string;
    audience3Id?: string;
    audience4Id?: string;
    reasonId: string;
  };

  const parseFunnelAudienceContent = (row: RowType): ReportTags => {
    // format 'funnelId,...audiencesIds,reasonId'
    // reasonId may be undefined ('') if no reason is selected
    const tags = row.displayGroupId.split(',');

    const funnelIdIdx = 0;
    const funnelId = tags.at(funnelIdIdx)!;

    const reasonIdIdx = -1;
    const reasonId = tags.at(reasonIdIdx)!;

    const audience1IdIdx = 1;
    const audienceIds = tags.slice(audience1IdIdx, reasonIdIdx);
    const audience1Id = audienceIds.shift()!;
    const audience2Id = audienceIds.shift();
    const audience3Id = audienceIds.shift();
    const audience4Id = audienceIds.shift();

    return {
      funnelId,
      audience1Id,
      audience2Id,
      audience3Id,
      audience4Id,
      reasonId,
    };
  };

  const getAudienceDisplayName = (audId: keyof ReportTags, row: RowType) => {
    const reportTags = parseFunnelAudienceContent(row);
    const audienceId = reportTags[audId];

    if (audienceId === 'control') return 'Control';
    if (audienceId === 'fallback') return 'Fallback';

    if (audienceId) {
      return rulesMap[audienceId]?.display_name ?? `Audience ${audienceId}`;
    }
  };

  const calculatedFields = ((row) => {
    return {
      vanityReasonName: () => {
        if (row.displayGroupId === 'None') return 'Unknown reason';

        const reportTags = parseFunnelAudienceContent(row);
        const vanityReasonId = reportTags.reasonId;

        if (vanityReasonId) {
          return (
            vanityReasonsMap[vanityReasonId]?.display_name ??
            `Reason ${vanityReasonId}`
          );
        }

        return 'No reason selected';
      },
      funnelDisplayName: () => {
        if (row.displayGroupId === 'None') return 'Unknown funnel';

        const reportTags = parseFunnelAudienceContent(row);
        const funnelId = reportTags.funnelId;

        if (funnelId) {
          return funnelsMap[funnelId]?.display_name ?? `Funnel ${funnelId}`;
        }

        return 'Unknown funnel';
      },
      audience1DisplayName: () => {
        // format 'funnelId,...audiencesIds,reasonId'
        // may be 0 or more audienceId
        if (row.displayGroupId === 'None') return 'Fallback';

        const audienceDisplayName = getAudienceDisplayName('audience1Id', row);

        return audienceDisplayName ?? 'Unknown audience';
      },
      audience2DisplayName: () => {
        return getAudienceDisplayName('audience2Id', row);
      },
      audience3DisplayName: () => {
        return getAudienceDisplayName('audience3Id', row);
      },
      audience4DisplayName: () => {
        return getAudienceDisplayName('audience4Id', row);
      },
      brandName: () => {
        if (row.displayGroupId === 'None') return 'Default';

        if (selectedBrand) {
          return selectedBrand.label;
        }

        return 'Unknown brand';
      },
      totalSessions: () => row.deflects + row.direct_canceled,
      deflectRate: () => {
        const totalSessions = row.deflects + row.direct_canceled;
        return (row.deflects / totalSessions) * 100;
      },
      lostDeflects: () => row.deflects - (row.cohort_saved + row.watchlists),
      saveRate: () =>
        (row.cohort_saved / (row.cohort_canceled + row.cohort_saved)) * 100,
      modalOfferAcceptRate: () =>
        (row.modal_offer_accepts / row.modal_offer_views) * 100,
      laOfferAcceptsRate: () =>
        (row.la_offer_accepts / row.la_offer_views) * 100,
      totalOfferAccepts: () => row.la_offer_accepts + row.modal_offer_accepts,
      passiveDeflects: () => row.deflects - row.offer_accept_deflects,
      passiveDeflectRate: () => {
        const totalSessions = row.deflects + row.direct_canceled;
        const passiveDeflects = row.deflects - row.offer_accept_deflects;

        return (passiveDeflects / totalSessions) * 100;
      },
      // Repurchase based metrics
      // deflect_payment_sessions // First Repurchase Order: # of first time repurchases post deflect
      // First Time Repurchase Rate: # of first time repurchases / Total saves for the cohort
      firstTimeRepurchaseRate: () =>
        (row.deflect_payment_sessions / row.deflects) * 100,
      // deflect_payment_revenue // Cumulative Repurchase Revenue (CRR) (total cumulative repurchase revenue driven by the cohort)
      // deflect_payments_count // Total # of repurchase orders
      // Avg. # of repurchase orders: # of repurchases / # or customers who repurchase
      avgNumRepurchaseOrders: () =>
        row.deflect_payments_count / row.deflect_payment_sessions,
      // Avg. value of repurchase orders: $ value of repurchases / # of repurchases
      avgValRepurchaseOrders: () =>
        row.deflect_payment_revenue / row.deflect_payments_count,
      // cancel_payment_sessions // First Winback Orders: # of first time repurchases post cancel
      // Winback Rate: # of repurchases from cancels / # of cancels
      winbackRate: () => {
        const lostDeflects = row.deflects - (row.cohort_saved + row.watchlists);
        return (
          (row.cancel_payment_sessions / (row.direct_canceled + lostDeflects)) *
          100
        );
      },
      // cancel_payment_revenue // Cumulative Winback Revenue (CRR) (total cumulative winback revenue driven by the cohort)
      // cancel_payments_count // Total # of winback orders
      // Avg. # of winback orders: # of winbacks / # or customers who winback
      avgNumWinbackOrders: () =>
        row.cancel_payments_count / row.cancel_payment_sessions,
      // Avg. value of winback orders: $ value of winbacks / # of winbacks
      avgValWinbackOrders: () =>
        row.cancel_payment_revenue / row.cancel_payments_count,
      activeDeflectRate: () =>
        row.deflects ? (row.deflect_still_paying / row.deflects) * 100 : 0,
    };
  }) satisfies PivotT.CalculatedFields<RowType>;

  type RowType = TrendsDatapoint;
  type PivotRow = PivotT.ComputedFields<RowType, typeof calculatedFields>;
  type PivotValues = PivotT.PivotValue<PivotRow>[];
  type GroupField = PivotT.GroupField<PivotRow>;

  const pivotRows = useMemo(() => {
    const result: GroupField[] = [
      {
        field: ['audience1DisplayName'],
        icon: NavIcons.audiences,
        name: 'audience',
        label: 'Audiences',
      },
      {
        field: ['funnelDisplayName'],
        icon: NavIcons.experiences,
        name: 'experience',
        label: 'Experiences',
      },
      {
        field: ['vanityReasonName'],
        icon: NavIcons.reasons,
        name: 'reason',
        label: 'Reasons',
      },
    ];

    if (isNestedAudiencesEnabled) {
      result[0]!.field = [
        'audience1DisplayName',
        'audience2DisplayName',
        'audience3DisplayName',
        'audience4DisplayName',
      ];
    }

    return result;
  }, [isNestedAudiencesEnabled]);

  const repurchasePivotValues: PivotValues = [
    {
      label: 'First repurchase order',
      field: 'deflect_payment_sessions',
      format: NumberKit.formatNumber,
    },
    {
      label: 'First time repurchase rate',
      field: 'firstTimeRepurchaseRate',
      format: NumberKit.formatPercent,
      // row.deflect_payment_sessions / (row.watchlists + row.cohort_saved)
      // First Repurchase Order / (Watchlist + Saved (cohort))
      tooltip: (row) => (
        <PivotTableTooltipContent
          formula="First Repurchase Order / Deflects"
          calculation={
            <>
              {row.deflect_payment_sessions} / {row.deflects} ={' '}
              {NumberKit.formatPercent(row.firstTimeRepurchaseRate)}
            </>
          }
        />
      ),
    },
    {
      label: 'Cumulative retained revenue',
      field: 'deflect_payment_revenue',
      format: NumberKit.formatMoney,
    },
    {
      label: 'Total # of repurchase orders',
      field: 'deflect_payments_count',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Avg. # of repurchase orders',
      field: 'avgNumRepurchaseOrders',
      format: NumberKit.formatNumberPrecision1,
    },
    {
      label: 'Avg. value of repurchase orders',
      field: 'avgValRepurchaseOrders',
      format: NumberKit.formatMoneyPrecision1,
    },
    {
      label: 'First winback orders',
      field: 'cancel_payment_sessions',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Winback rate',
      field: 'winbackRate',
      format: NumberKit.formatPercent,
      // row.cancel_payment_sessions / (row.direct_canceled + lostDeflects)
      // First Winback Orders / (Canceled + Lost Deflects)
      tooltip: (row) => (
        <PivotTableTooltipContent
          formula={`First Winback Orders / (Canceled + Lost Deflects)`}
          calculation={
            <>
              {row.cancel_payment_sessions} / ({row.direct_canceled} +{' '}
              {row.lostDeflects}) = {NumberKit.formatPercent(row.winbackRate)}
            </>
          }
        />
      ),
    },
    {
      label: 'Cumulative winback revenue',
      field: 'cancel_payment_revenue',
      format: NumberKit.formatMoney,
    },
    {
      label: 'Total # of winback orders',
      field: 'cancel_payments_count',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Avg. # of winback orders',
      field: 'avgNumWinbackOrders',
      format: NumberKit.formatNumberPrecision1,
    },
    {
      label: 'Avg. value of winback orders',
      field: 'avgValWinbackOrders',
      format: NumberKit.formatMoneyPrecision1,
    },
    {
      label: '% of cohort still paying',
      field: 'activeDeflectRate',
      format: NumberKit.formatPercent,
      tooltip: (row) => (
        <PivotTableTooltipContent
          formula="Active deflects / Total deflects"
          calculation={
            <>
              {row.deflect_still_paying} / {row.deflects} ={' '}
              {row.deflects === 0
                ? '--'
                : NumberKit.formatPercent(row.activeDeflectRate)}
            </>
          }
        />
      ),
    },
  ];

  const genericPivotValues: PivotValues = [
    {
      label: 'Total sessions',
      field: 'totalSessions',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Canceled',
      field: 'direct_canceled',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Watchlist',
      field: 'watchlists',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Saved',
      field: 'cohort_saved',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Deflect rate',
      field: 'deflectRate',
      format: NumberKit.formatPercent,
      tooltip: (row) => (
        <PivotTableTooltipContent
          formula="Deflects / Total sessions"
          calculation={
            <>
              {row.deflects} / {row.totalSessions} ={' '}
              {NumberKit.formatPercent(row.deflectRate)}
            </>
          }
        />
      ),
    },
    {
      label: 'Lost Deflects',
      field: 'lostDeflects',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Save rate',
      field: 'saveRate',
      format: NumberKit.formatPercent,
      // row.cohort_saved / (row.cohort_canceled + row.cohort_saved)
      // Saved / (Canceled (non-direct) + Saved)
      tooltip: (row) => (
        <PivotTableTooltipContent
          formula="Saved / (Canceled + Saved)"
          calculation={
            <>
              {row.cohort_saved} / ({row.cohort_canceled} + {row.cohort_saved})
              {' = '}
              {NumberKit.formatPercent(row.saveRate)}
            </>
          }
        />
      ),
    },
    {
      label: 'Modal offer accepts',
      field: 'modal_offer_accepts',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Modal offer views',
      field: 'modal_offer_views',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Modal offer accept rate',
      field: 'modalOfferAcceptRate',
      format: NumberKit.formatPercent,
      // row.modal_offer_accepts / row.modal_offer_views
      // Modal offer accepts / Modal offer views
      tooltip: (row) => (
        <PivotTableTooltipContent
          formula="Modal offer accepts / Modal offer views"
          calculation={
            <>
              {row.modal_offer_accepts} / {row.modal_offer_views} ={' '}
              {NumberKit.formatPercent(row.modalOfferAcceptRate)}
            </>
          }
        />
      ),
    },
    {
      label: 'LA offer accepts',
      field: 'la_offer_accepts',
      format: NumberKit.formatNumber,
    },
    {
      label: 'LA offer views',
      field: 'la_offer_views',
      format: NumberKit.formatNumber,
    },
    {
      label: 'LA offer accept rate',
      field: 'laOfferAcceptsRate',
      format: NumberKit.formatPercent,
      // row.la_offer_accepts / row.la_offer_views
      // LA offer accepts / LA offer views
      tooltip: (row) => (
        <PivotTableTooltipContent
          formula="LA offer accepts / LA offer views"
          calculation={
            <>
              {row.la_offer_accepts} / {row.la_offer_views} ={' '}
              {NumberKit.formatPercent(row.laOfferAcceptsRate)}
            </>
          }
        />
      ),
    },
    {
      label: 'Total offer accepts',
      field: 'totalOfferAccepts',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Passive deflects',
      field: 'passiveDeflects',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Deflects',
      field: 'deflects',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Active deflects',
      field: 'offer_accept_deflects',
      format: NumberKit.formatNumber,
    },
    {
      label: 'Passive deflects rate',
      field: 'passiveDeflectRate',
      format: NumberKit.formatPercent,
      // passiveDeflects / totalSessions
      // Passive deflects / Total sessions
      tooltip: (row) => (
        <PivotTableTooltipContent
          formula="Passive deflects / Total sessions"
          calculation={
            <>
              {row.passiveDeflects} / {row.totalSessions} ={' '}
              {NumberKit.formatPercent(row.passiveDeflectRate)}
            </>
          }
        />
      ),
    },
  ];

  const pivotValues = isRepurchaseAttributionEnabled
    ? [...genericPivotValues, ...repurchasePivotValues]
    : genericPivotValues;

  return (
    <div>
      <div className="tw-mb-3 tw-mt-2 tw-flex tw-items-center tw-gap-2">
        <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-primary-100 tw-bg-primary-50 tw-p-2">
          <SIcon name="circle-x" size={18} className="tw-text-primary-600" />
        </div>
        <span className="tw-text-lg tw-font-semibold tw-text-neutral-900">
          Experience performance report
        </span>
      </div>
      <div className="tw-text-sm tw-font-light tw-text-neutral-600">
        Select an audience to show a breakdown by experience (cancel page).
      </div>
      <div className="tw-mb-6">
        <PivotTableComponent
          tableName="ChargebeeRetention_Experience_Performance"
          isFetching={isFetching}
          isExportDisabled={isStarterFreePlan || isDemoData}
          exportTooltip={isStarterFreePlan ? exportBtnTooltip : undefined}
          data={data}
          calculatedFields={calculatedFields}
          pivotValues={pivotValues}
          pivotRows={pivotRows}
          visibleColumns={
            initVisibleColumns ?? [
              'totalSessions',
              'direct_canceled',
              'watchlists',
              'cohort_saved',
              'deflectRate',
              'lostDeflects',
              'saveRate',
            ]
          }
          groupByOpts={initGroupByOpts ?? ['experience', 'reason']}
          onVisibleColumnsChange={(value) => {
            dispatch(
              appPersistSlice.actions.saveExpPerfReportNavState({
                appKey,
                visibleColumns: value,
              })
            );
          }}
          onGroupByOptsChange={(value) => {
            dispatch(
              appPersistSlice.actions.saveExpPerfReportNavState({
                appKey,
                groupBy: value.filter(isDefined),
              })
            );
          }}
        />
      </div>
    </div>
  );
};

export default PlayExpPerf;
